import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CustomerListTable from './components/CustomerListTable';
import CustomerListCards from './components/CustomerListCards';
import CustomerDetailPanel from './components/CustomerDetailPanel';
import CustomerFormModal from './components/CustomerFormModal';
import { customerService } from '../../services/customerService';
import { supabase } from '../../lib/supabase';
import { createRealtimeSubscription, RealtimeErrorType } from '../../utils/realtimeErrorHandler';

const ALL_COLUMNS = [
  { key: 'companyName', label: 'Company Name', sortable: true, required: true },
  { key: 'contact', label: 'Contact', sortable: false },
  { key: 'activeProjects', label: 'Active Projects', sortable: true },
  { key: 'totalValue', label: 'Total Contract Value', sortable: true },
  { key: 'bidConversionRate', label: 'Conversion Rate', sortable: true },
  { key: 'actions', label: 'Actions', sortable: false, required: true },
];

const DEFAULT_VISIBLE = ['companyName', 'contact', 'activeProjects', 'totalValue', 'bidConversionRate', 'actions'];
const DEFAULT_FILTERS = { search: '', projectStatus: 'all', contractValue: 'all' };
const DEFAULT_SORT = { key: 'companyName', direction: 'asc' };

const CustomerManagement = () => {
  const navigate = useNavigate();

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [realtimeError, setRealtimeError] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);

  // Table preferences
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [sortConfig, setSortConfig] = useState(DEFAULT_SORT);
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const columnPickerRef = useRef(null);

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [deleteConfirmCustomer, setDeleteConfirmCustomer] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState(new Set());
  const [bulkDeleteCustomers, setBulkDeleteCustomers] = useState(null);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const handleRealtimeError = (error) => {
    setRealtimeError(error);
    setIsRetrying(error?.willRetry || false);
    if (!error?.willRetry && error?.type !== RealtimeErrorType?.CONNECTION_FAILED) {
      setTimeout(() => setRealtimeError(null), 5000);
    }
  };

  useEffect(() => {
    fetchCustomers();
    loadPreferences();

    const { cleanup } = createRealtimeSubscription({
      supabase,
      channelName: 'customers_changes',
      tableName: 'customers',
      onDataChange: () => { fetchCustomers(); },
      onError: handleRealtimeError
    });

    return cleanup;
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (columnPickerRef?.current && !columnPickerRef?.current?.contains(e?.target)) {
        setShowColumnPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadPreferences = async () => {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return;
      const { data, error: prefError } = await supabase
        ?.from('user_customer_table_preferences')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.single();
      if (data && !prefError) {
        setVisibleColumns(data?.visible_columns || DEFAULT_VISIBLE);
        const savedFilters = data?.saved_filters || DEFAULT_FILTERS;
        setFilters(savedFilters);
        setSortConfig({ key: data?.sort_column || 'companyName', direction: data?.sort_direction || 'asc' });
      }
    } catch (err) {
      // Use defaults
    }
  };

  const savePreferences = async (newVisible, newFilters, newSort) => {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return;
      await supabase
        ?.from('user_customer_table_preferences')
        ?.upsert({
          user_id: user?.id,
          visible_columns: newVisible,
          saved_filters: newFilters,
          sort_column: newSort?.key,
          sort_direction: newSort?.direction,
        }, { onConflict: 'user_id' });
    } catch (err) {
      console.error('Failed to save customer preferences:', err?.message);
    }
  };

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error: fetchError } = await customerService?.getAll();
      if (fetchError) {
        setError(fetchError?.message || 'Failed to fetch customers');
      } else {
        setCustomers(data || []);
      }
    } catch (err) {
      setError(err?.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    savePreferences(visibleColumns, newFilters, sortConfig);
  };

  const handleClearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    savePreferences(visibleColumns, DEFAULT_FILTERS, sortConfig);
  };

  const handleSort = (key) => {
    const newSort = {
      key,
      direction: sortConfig?.key === key && sortConfig?.direction === 'asc' ? 'desc' : 'asc'
    };
    setSortConfig(newSort);
    savePreferences(visibleColumns, filters, newSort);
  };

  const handleToggleColumn = (colKey) => {
    const col = ALL_COLUMNS?.find(c => c?.key === colKey);
    if (col?.required) return;
    const newVisible = visibleColumns?.includes(colKey)
      ? visibleColumns?.filter(k => k !== colKey)
      : [...visibleColumns, colKey];
    setVisibleColumns(newVisible);
    savePreferences(newVisible, filters, sortConfig);
  };

  const filteredAndSortedCustomers = useMemo(() => {
    let filtered = [...customers];

    if (filters?.search) {
      const search = filters?.search?.toLowerCase();
      filtered = filtered?.filter(
        (customer) =>
          customer?.companyName?.toLowerCase()?.includes(search) ||
          customer?.contactPerson?.toLowerCase()?.includes(search) ||
          customer?.email?.toLowerCase()?.includes(search)
      );
    }

    if (filters?.projectStatus !== 'all') {
      filtered = filtered?.filter((customer) => {
        if (filters?.projectStatus === 'active') return customer?.activeProjects > 0;
        if (filters?.projectStatus === 'completed') return customer?.activeProjects === 0 && customer?.totalBids > 0;
        if (filters?.projectStatus === 'none') return customer?.activeProjects === 0;
        return true;
      });
    }

    if (filters?.contractValue !== 'all') {
      filtered = filtered?.filter((customer) => {
        const value = customer?.totalValue;
        if (filters?.contractValue === '0-50000') return value >= 0 && value <= 50000;
        if (filters?.contractValue === '50000-100000') return value > 50000 && value <= 100000;
        if (filters?.contractValue === '100000-250000') return value > 100000 && value <= 250000;
        if (filters?.contractValue === '250000+') return value > 250000;
        return true;
      });
    }

    filtered?.sort((a, b) => {
      let aValue = a?.[sortConfig?.key];
      let bValue = b?.[sortConfig?.key];
      if (typeof aValue === 'string') {
        aValue = aValue?.toLowerCase();
        bValue = bValue?.toLowerCase();
      }
      if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [customers, filters, sortConfig]);

  const handleExport = () => {
    const csvContent = [
      ['Company Name', 'Contact Person', 'Email', 'Phone', 'Active Projects', 'Total Value'],
      ...filteredAndSortedCustomers?.map((c) => [
        c?.companyName, c?.contactPerson, c?.email, c?.phone, c?.activeProjects, c?.totalValue
      ])
    ]?.map((row) => row?.join(','))?.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `customers_export_${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    a?.click();
  };

  const handleAddCustomer = () => { setEditingCustomer(null); setIsFormModalOpen(true); };
  const handleEditCustomer = (customer) => { setEditingCustomer(customer); setIsFormModalOpen(true); };

  const handleSaveCustomer = async (formData) => {
    try {
      setError(null);
      if (editingCustomer) {
        const { data, error: updateError } = await customerService?.update(editingCustomer?.id, formData);
        if (updateError) { setError(updateError?.message || 'Failed to update customer'); return; }
        setCustomers((prev) => prev?.map((c) => (c?.id === editingCustomer?.id ? data : c)));
      } else {
        const { data, error: createError } = await customerService?.create(formData);
        if (createError) { setError(createError?.message || 'Failed to create customer'); return; }
        setCustomers((prev) => [data, ...prev]);
      }
      setIsFormModalOpen(false);
      setEditingCustomer(null);
    } catch (err) {
      setError(err?.message || 'An unexpected error occurred');
    }
  };

  const handleDeleteCustomer = (customer) => { setDeleteConfirmCustomer(customer); };

  const handleSelectCustomer = (id) => {
    setSelectedCustomerIds(prev => {
      const next = new Set(prev);
      if (next?.has(id)) next?.delete(id);
      else next?.add(id);
      return next;
    });
  };

  const handleSelectAllCustomers = () => {
    const allIds = filteredAndSortedCustomers?.map(c => c?.id);
    const allSelected = allIds?.every(id => selectedCustomerIds?.has(id));
    if (allSelected) {
      setSelectedCustomerIds(prev => {
        const next = new Set(prev);
        allIds?.forEach(id => next?.delete(id));
        return next;
      });
    } else {
      setSelectedCustomerIds(prev => {
        const next = new Set(prev);
        allIds?.forEach(id => next?.add(id));
        return next;
      });
    }
  };

  const handleBulkDeleteCustomers = () => {
    const selected = filteredAndSortedCustomers?.filter(c => selectedCustomerIds?.has(c?.id));
    setBulkDeleteCustomers(selected);
  };

  const confirmBulkDeleteCustomers = async () => {
    if (!bulkDeleteCustomers?.length) return;
    setIsBulkDeleting(true);
    try {
      await Promise.all(bulkDeleteCustomers?.map(c => customerService?.delete(c?.id)));
      setCustomers(prev => prev?.filter(c => !bulkDeleteCustomers?.some(d => d?.id === c?.id)));
      if (selectedCustomer && bulkDeleteCustomers?.some(d => d?.id === selectedCustomer?.id)) {
        setSelectedCustomer(null);
      }
      setSelectedCustomerIds(new Set());
      setBulkDeleteCustomers(null);
    } catch (err) {
      setError(err?.message || 'Failed to delete customers');
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteConfirmCustomer) return;
    try {
      setIsDeleting(true);
      setError(null);
      const { error: deleteError } = await customerService?.delete(deleteConfirmCustomer?.id);
      if (deleteError) { setError(deleteError?.message || 'Failed to delete customer'); return; }
      setCustomers((prev) => prev?.filter((c) => c?.id !== deleteConfirmCustomer?.id));
      setDeleteConfirmCustomer(null);
      if (selectedCustomer?.id === deleteConfirmCustomer?.id) setSelectedCustomer(null);
    } catch (err) {
      setError(err?.message || 'An unexpected error occurred');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleViewDetails = (customer) => { setSelectedCustomer(customer); };
  const handleNavigateToBids = (customer) => { navigate('/bid-management', { state: { customerId: customer?.id } }); };
  const handleNavigateToProjects = (customer) => { navigate('/project-management', { state: { customerId: customer?.id } }); };

  const hasActiveFilters = filters?.search || filters?.projectStatus !== 'all' || filters?.contractValue !== 'all';

  const selectedCustomerCount = [...selectedCustomerIds]?.filter(id =>
    filteredAndSortedCustomers?.some(c => c?.id === id)
  )?.length;

  const allCustomersSelected = filteredAndSortedCustomers?.length > 0 &&
    filteredAndSortedCustomers?.every(c => selectedCustomerIds?.has(c?.id));
  const someCustomersSelected = filteredAndSortedCustomers?.some(c => selectedCustomerIds?.has(c?.id)) && !allCustomersSelected;

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Real-time Error Alert */}
        {realtimeError && (
          <div className={`rounded-lg border p-4 flex items-start gap-3 ${
            realtimeError?.type === RealtimeErrorType?.CONNECTION_FAILED
              ? 'bg-red-50 border-red-200 text-red-800' : 'bg-yellow-50 border-yellow-200 text-yellow-800'
          }`}>
            <Icon name={isRetrying ? 'RefreshCw' : 'AlertTriangle'} size={20} className={isRetrying ? 'animate-spin' : ''} />
            <div className="flex-1">
              <p className="font-medium text-sm">{realtimeError?.message}</p>
              {realtimeError?.attemptCount && (
                <p className="text-xs mt-1 opacity-80">Retry attempt {realtimeError?.attemptCount}/3</p>
              )}
            </div>
            {!isRetrying && realtimeError?.type === RealtimeErrorType?.CONNECTION_FAILED && (
              <button onClick={() => window.location?.reload()} className="text-sm font-medium underline hover:no-underline">Refresh Page</button>
            )}
            {!isRetrying && realtimeError?.type !== RealtimeErrorType?.CONNECTION_FAILED && (
              <button onClick={() => setRealtimeError(null)} className="text-sm font-medium"><Icon name="X" size={16} /></button>
            )}
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">Customer Management</h1>
            <p className="text-sm md:text-base text-muted-foreground">
              Manage customer relationships, track projects, and monitor contract values
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-muted rounded-lg p-1">
              <button
                onClick={() => setViewMode('table')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'table' ? 'bg-card text-accent shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name="Table" size={18} />
              </button>
              <button
                onClick={() => setViewMode('cards')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'cards' ? 'bg-card text-accent shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name="LayoutGrid" size={18} />
              </button>
            </div>
            <Button variant="default" onClick={handleAddCustomer} iconName="Plus" iconPosition="left" disabled={loading}>
              Add Customer
            </Button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="AlertCircle" size={20} className="text-destructive mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-destructive mb-1">Error</h3>
                <p className="text-sm text-destructive/90">{error}</p>
              </div>
              <button onClick={() => setError(null)} className="text-destructive/70 hover:text-destructive">
                <Icon name="X" size={16} />
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-muted-foreground">Loading customers...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              <div className="bg-card border border-border rounded-xl p-4 md:p-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-accent/10 flex items-center justify-center">
                    <Icon name="Users" size={20} className="text-accent md:w-6 md:h-6" />
                  </div>
                  <Icon name="TrendingUp" size={16} className="text-success" />
                </div>
                <p className="text-2xl md:text-3xl font-bold text-foreground mb-1">{customers?.length}</p>
                <p className="text-xs md:text-sm text-muted-foreground">Total Customers</p>
              </div>
              <div className="bg-card border border-border rounded-xl p-4 md:p-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-success/10 flex items-center justify-center">
                    <Icon name="Briefcase" size={20} className="text-success md:w-6 md:h-6" />
                  </div>
                  <Icon name="TrendingUp" size={16} className="text-success" />
                </div>
                <p className="text-2xl md:text-3xl font-bold text-foreground mb-1">
                  {customers?.reduce((sum, c) => sum + c?.activeProjects, 0)}
                </p>
                <p className="text-xs md:text-sm text-muted-foreground">Active Projects</p>
              </div>
              <div className="bg-card border border-border rounded-xl p-4 md:p-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-warning/10 flex items-center justify-center">
                    <Icon name="DollarSign" size={20} className="text-warning md:w-6 md:h-6" />
                  </div>
                  <Icon name="TrendingUp" size={16} className="text-success" />
                </div>
                <p className="text-2xl md:text-3xl font-bold text-foreground mb-1">
                  ${(customers?.reduce((sum, c) => sum + c?.totalValue, 0) / 1000000)?.toFixed(2)}M
                </p>
                <p className="text-xs md:text-sm text-muted-foreground">Total Contract Value</p>
              </div>
              <div className="bg-card border border-border rounded-xl p-4 md:p-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Icon name="TrendingUp" size={20} className="text-primary md:w-6 md:h-6" />
                  </div>
                  <Icon name="TrendingUp" size={16} className="text-success" />
                </div>
                <p className="text-2xl md:text-3xl font-bold text-foreground mb-1">
                  {Math.round(customers?.reduce((sum, c) => sum + c?.bidConversionRate, 0) / customers?.length)}%
                </p>
                <p className="text-xs md:text-sm text-muted-foreground">Avg Conversion Rate</p>
              </div>
            </div>

            {/* Filter Bar with Column Picker */}
            <div className="bg-card rounded-xl border border-border p-4">
              <div className="flex flex-col md:flex-row gap-3 items-start md:items-center">
                {/* Search */}
                <div className="relative flex-1 min-w-0">
                  <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search customers..."
                    value={filters?.search}
                    onChange={e => handleFilterChange('search', e?.target?.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
                  />
                </div>
                {/* Project Status Filter */}
                <select
                  value={filters?.projectStatus}
                  onChange={e => handleFilterChange('projectStatus', e?.target?.value)}
                  className="px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
                >
                  <option value="all">All Projects</option>
                  <option value="active">Has Active Projects</option>
                  <option value="completed">Completed Only</option>
                  <option value="none">No Projects</option>
                </select>
                {/* Contract Value Filter */}
                <select
                  value={filters?.contractValue}
                  onChange={e => handleFilterChange('contractValue', e?.target?.value)}
                  className="px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
                >
                  <option value="all">All Values</option>
                  <option value="0-50000">$0 - $50K</option>
                  <option value="50000-100000">$50K - $100K</option>
                  <option value="100000-250000">$100K - $250K</option>
                  <option value="250000+">$250K+</option>
                </select>
                {/* Export */}
                <button
                  onClick={handleExport}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm border border-border rounded-lg bg-background hover:bg-muted/50 text-foreground transition-colors whitespace-nowrap"
                >
                  <Icon name="Download" size={14} />
                  Export
                </button>
                {/* Clear Filters */}
                {hasActiveFilters && (
                  <button
                    onClick={handleClearFilters}
                    className="flex items-center gap-1.5 px-3 py-2 text-sm text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted/50 transition-colors whitespace-nowrap"
                  >
                    <Icon name="X" size={14} />
                    Clear
                  </button>
                )}
                {/* Column Picker */}
                {viewMode === 'table' && (
                  <div className="relative" ref={columnPickerRef}>
                    <button
                      onClick={() => setShowColumnPicker(v => !v)}
                      className="flex items-center gap-1.5 px-3 py-2 text-sm border border-border rounded-lg bg-background hover:bg-muted/50 text-foreground transition-colors whitespace-nowrap"
                    >
                      <Icon name="Columns" size={14} />
                      Columns
                      <span className="ml-1 text-xs bg-accent/10 text-accent rounded px-1">
                        {visibleColumns?.filter(k => k !== 'actions')?.length}
                      </span>
                    </button>
                    {showColumnPicker && (
                      <div className="absolute right-0 top-full mt-1 z-50 bg-card border border-border rounded-xl shadow-lg p-3 min-w-[200px]">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 px-1">Show Columns</p>
                        {ALL_COLUMNS?.filter(c => c?.key !== 'actions')?.map(col => (
                          <label key={col?.key} className={`flex items-center gap-2 px-1 py-1.5 rounded cursor-pointer hover:bg-muted/50 ${col?.required ? 'opacity-60 cursor-not-allowed' : ''}`}>
                            <input
                              type="checkbox"
                              checked={visibleColumns?.includes(col?.key)}
                              onChange={() => handleToggleColumn(col?.key)}
                              disabled={col?.required}
                              className="rounded border-border text-accent focus:ring-accent/30"
                            />
                            <span className="text-sm text-foreground">{col?.label}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Bulk Action Toolbar */}
            {selectedCustomerCount > 0 && (
              <div className="flex items-center gap-3 px-4 py-3 bg-accent/5 border border-accent/20 rounded-xl">
                <div className="flex items-center gap-2 flex-1">
                  <div className="w-6 h-6 rounded-full bg-accent flex items-center justify-center">
                    <span className="text-xs font-bold text-white">{selectedCustomerCount}</span>
                  </div>
                  <span className="text-sm font-medium text-foreground">
                    {selectedCustomerCount} customer{selectedCustomerCount !== 1 ? 's' : ''} selected
                  </span>
                </div>
                <button
                  onClick={handleBulkDeleteCustomers}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-destructive text-white rounded-lg hover:bg-destructive/90 transition-colors"
                >
                  <Icon name="Trash2" size={14} />
                  Delete Selected
                </button>
                <button
                  onClick={() => setSelectedCustomerIds(new Set())}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <Icon name="X" size={14} />
                  Clear
                </button>
              </div>
            )}

            {/* Customer List */}
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              {filteredAndSortedCustomers?.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 md:py-16 px-4">
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-muted flex items-center justify-center mb-4">
                    <Icon name="Users" size={32} className="text-muted-foreground md:w-10 md:h-10" />
                  </div>
                  <h3 className="text-lg md:text-xl font-semibold text-foreground mb-2">No customers found</h3>
                  <p className="text-sm md:text-base text-muted-foreground text-center mb-6">
                    {hasActiveFilters ? 'Try adjusting your filters' : 'Get started by adding your first customer'}
                  </p>
                  {!hasActiveFilters && (
                    <Button variant="default" onClick={handleAddCustomer} iconName="Plus" iconPosition="left">
                      Add Customer
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  {viewMode === 'table' ? (
                    <div className="hidden md:block">
                      <CustomerListTable
                        customers={filteredAndSortedCustomers}
                        onViewDetails={handleViewDetails}
                        onEdit={handleEditCustomer}
                        onDelete={handleDeleteCustomer}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        visibleColumns={visibleColumns}
                        selectedIds={selectedCustomerIds}
                        onSelectRow={handleSelectCustomer}
                        onSelectAll={handleSelectAllCustomers}
                        allSelected={allCustomersSelected}
                        someSelected={someCustomersSelected}
                      />
                    </div>
                  ) : null}
                  {viewMode === 'cards' || viewMode === 'table' ? (
                    <div className={viewMode === 'table' ? 'md:hidden p-4' : 'p-4'}>
                      <CustomerListCards
                        customers={filteredAndSortedCustomers}
                        onViewDetails={handleViewDetails}
                        onEdit={handleEditCustomer}
                        onDelete={handleDeleteCustomer}
                        selectedIds={selectedCustomerIds}
                        onSelectRow={handleSelectCustomer}
                      />
                    </div>
                  ) : null}
                </>
              )}
            </div>

            {filteredAndSortedCustomers?.length > 0 && (
              <div className="text-center">
                <p className="text-sm text-muted-foreground">
                  Showing {filteredAndSortedCustomers?.length} of {customers?.length} customers
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {selectedCustomer && (
        <CustomerDetailPanel
          customer={selectedCustomer}
          onClose={() => setSelectedCustomer(null)}
          onEdit={handleEditCustomer}
          onNavigateToBids={handleNavigateToBids}
          onNavigateToProjects={handleNavigateToProjects}
        />
      )}
      <CustomerFormModal
        isOpen={isFormModalOpen}
        onClose={() => { setIsFormModalOpen(false); setEditingCustomer(null); }}
        onSave={handleSaveCustomer}
        customer={editingCustomer}
      />
      {bulkDeleteCustomers && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
          <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
                  <Icon name="AlertTriangle" size={24} className="text-destructive" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground">Delete {bulkDeleteCustomers?.length} Customer{bulkDeleteCustomers?.length !== 1 ? 's' : ''}</h3>
                  <p className="text-sm text-muted-foreground">This action cannot be undone</p>
                </div>
              </div>
              <p className="text-sm text-foreground mb-6">
                Are you sure you want to delete <span className="font-semibold">{bulkDeleteCustomers?.length} customer{bulkDeleteCustomers?.length !== 1 ? 's' : ''}</span>? This will permanently remove them and all associated data.
              </p>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setBulkDeleteCustomers(null)} disabled={isBulkDeleting} className="flex-1">Cancel</Button>
                <Button variant="destructive" onClick={confirmBulkDeleteCustomers} disabled={isBulkDeleting} className="flex-1">
                  {isBulkDeleting ? 'Deleting...' : `Delete ${bulkDeleteCustomers?.length}`}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      {deleteConfirmCustomer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
          <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
                  <Icon name="AlertTriangle" size={24} className="text-destructive" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground">Delete Customer</h3>
                  <p className="text-sm text-muted-foreground">This action cannot be undone</p>
                </div>
              </div>
              <p className="text-sm text-foreground mb-6">
                Are you sure you want to delete <span className="font-semibold">{deleteConfirmCustomer?.companyName}</span>? This will permanently remove the customer and all associated data.
              </p>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setDeleteConfirmCustomer(null)} disabled={isDeleting} className="flex-1">Cancel</Button>
                <Button variant="destructive" onClick={confirmDelete} disabled={isDeleting} className="flex-1">
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default CustomerManagement;